import cx from 'classnames'
import { format } from 'date-fns'
import * as Duration from 'dration'
import { timeago } from '@/utils/date-util'
import { Tooltip } from '@/components/Tooltip'
import { Icon } from '@/components/Icon'
import { CellText, type CellTextProps } from '@/components/DataTable/CellText'
import { StatusGlyph } from '@/features/jobs/components/StatusGlyph'
import styles from './JobListCells.module.css'
import type { Glyph, Tone } from '@/features/jobs/job-status-display'

export interface StatusCellProps {
  glyph: Glyph
  tone: Tone
  label: string
}

export function StatusCell({ glyph, tone, label }: StatusCellProps) {
  return (
    <span className={styles.status}>
      <StatusGlyph glyph={glyph} tone={tone} />
      <CellText variant="truncate">{label}</CellText>
    </span>
  )
}

export interface PrimaryCellProps {
  primary: string
  secondary?: string
  unreadCount: number
  openTicket: { statusMessage: string } | null
  testId?: string
}

export function PrimaryCell({
  primary,
  secondary,
  unreadCount: unread,
  openTicket,
  testId,
}: PrimaryCellProps) {
  return (
    <span className={styles.primary}>
      <span className={styles.primaryText} data-test={testId}>
        {primary}
      </span>
      {secondary && secondary !== primary && (
        <span className={styles.secondaryText}>{secondary}</span>
      )}
      {unread > 0 && (
        <span
          className={styles.unread}
          title={`${unread} unread ${unread === 1 ? 'message' : 'messages'}`}
        >
          <Icon auto name="chat-bubble" />
          {unread}
        </span>
      )}
      {openTicket && (
        <Tooltip
          placement="top-start"
          content={openTicket.statusMessage || 'This job has an open ticket'}
        >
          <span className={styles.ticket}>Open ticket</span>
        </Tooltip>
      )}
    </span>
  )
}

export interface WaitingOnCellProps {
  label: string
  isYou: boolean
}

export function WaitingOnCell({ label, isYou }: WaitingOnCellProps) {
  return (
    <span className={cx(styles.waiting, isYou && styles.waitingYou)}>
      {label}
    </span>
  )
}

export interface DueCellProps {
  text: string
  tone: CellTextProps['tone']
  title: string
}

export function DueCell({ text, tone, title }: DueCellProps) {
  return (
    <CellText variant="tabular" tone={tone} title={title}>
      {text}
    </CellText>
  )
}

export interface RelativeDateCellProps {
  date: Date | null | undefined
}

export function RelativeDateCell({ date }: RelativeDateCellProps) {
  if (!date) {
    return <EmptyCell />
  }

  return (
    <CellText variant="tabular" title={format(date, 'PPpp')}>
      {timeago(date, { cutoff: Duration.fromDays(7) })}
    </CellText>
  )
}

export interface CalendarDateCellProps {
  date: Date | null
}

export function CalendarDateCell({ date }: CalendarDateCellProps) {
  return date ? (
    <CellText variant="tabular">{format(date, 'MMM d, yyyy')}</CellText>
  ) : (
    <EmptyCell />
  )
}

export interface TextCellProps {
  text: string
}

export function TruncatedTextCell({ text }: TextCellProps) {
  return <CellText variant="truncate">{text}</CellText>
}

export function MonoTextCell({ text }: TextCellProps) {
  return <CellText variant="mono">{text}</CellText>
}

export function EmptyCell() {
  return <CellText variant="empty">—</CellText>
}
